import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { SetRequired } from 'type-fest';

import * as PackageInventoryStore from '../../../redux/packageInventory';
import { FilterPanel, QualityRequired } from '../../generic';
import ProjectLabels from '../../projects/dashboardV2/ProjectFilters/ProjectLabels';
import PackageInventoryFiltersPackageDepth from './PackageDepth';
import PackageInventoryFiltersProjectSource from './ProjectSource';
import PackageInventoryFiltersProjectVisibility from './ProjectVisibility';
import type { MultiSelectFilterOption } from '../../generic/Filter/MultiSelectFilter';
import useFetchPackages from '../hooks/useFetchPackages';
import useFetchPackageVersionProjects from '../hooks/useFetchPackageVersionProjects';
import type { PackageInventoryParams, WithResetSelectedPackage } from '../PackageInventoryContainer';
import type { AnyFilter } from '../../../../shared/organizationPackages';
import PackageBlockType from './PackageBlockType';
import ProjectNameFilters from './ProjectNameFilters';

type Props = {
  labels: AnyFilter['labels'];
  projectName: string;
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to searching
 * by project name, import method, visibility, or project label.
 */
const PackageInventoryFiltersProject: VoidFunctionComponent<Props> = ({
  labels,
  projectName,
  setFilters,
}) => (
  <FilterPanel title="Project">
    <ProjectNameFilters projectName={projectName} setFilters={setFilters} />
    <PackageInventoryFiltersProjectSource setFilters={setFilters} />
    <PackageInventoryFiltersProjectVisibility setFilters={setFilters} />
    <ProjectLabels
      filterLabels={labels ?? []}
      onChange={(items: MultiSelectFilterOption[]) => setFilters({ labels: items.map(item => item.value) })}
      testId="package-inventory-filters--project-labels"
    />
    <PackageInventoryFiltersPackageDepth setFilters={setFilters} />
    <QualityRequired>
      <PackageBlockType setFilters={setFilters} />
    </QualityRequired>
  </FilterPanel>
);

/**
 * Stateful container for the project filters as they are on the main /packages page.
 */
export const PackageInventoryFiltersProjectPackagesContainer: VoidFunctionComponent<{ withResetSelectedPackage: WithResetSelectedPackage; }> = ({ // eslint-disable-line max-len -- It's fine.
  withResetSelectedPackage,
}) => {
  const labels = useSelector(PackageInventoryStore.selectors.getFiltersProjectLabels);
  const projectName = useSelector(PackageInventoryStore.selectors.getFiltersProjectName);
  const setFilters = useFetchPackages(PackageInventoryStore.actions.setFilters);
  return (
    <PackageInventoryFiltersProject
      labels={labels}
      projectName={projectName}
      setFilters={withResetSelectedPackage(setFilters)}
    />
  );
};

type PackageInventoryFiltersProjectVersionContainerProps = {
  params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;
};

/**
 * Stateful container for the project filters as they are on the /packages/:locator/:version page.
 */
export const PackageInventoryFiltersProjectVersionContainer: VoidFunctionComponent<PackageInventoryFiltersProjectVersionContainerProps> = ({ // eslint-disable-line max-len -- It's fine.
  params,
}) => {
  const labels = useSelector(PackageInventoryStore.selectors.getFiltersProjectLabels);
  const projectName = useSelector(PackageInventoryStore.selectors.getFiltersProjectName);
  const setFilters = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setFilters,
    params.packageLocator,
    params.version,
  );
  return (
    <PackageInventoryFiltersProject
      labels={labels}
      projectName={projectName}
      setFilters={setFilters}
    />
  );
};
